/* Settings ▸ Permit Library ▸ Permit Row Colors — the New / Edit DETAIL VIEW (BO-UI Standard v4 §4
 * "Detail View — fixed anatomy", CLAUDE.md standing rule 12).
 *
 * Full page, never a popup: header (`Permit Row Color – {record}` · breadcrumb · prev/next record
 * arrows · the view-only Audit chip) · the action bar with the XAF save set Save · Save and Close ·
 * Save and New · | · Cancel (all three through useSaveFlow, only the after-step differs) · General
 * Information as label-left rows (sd-grid--side) · the Conditions · a Preview grid painted with the
 * draft rule's own colour. Owner request 2026-10-23: the action bar is shown at the TOP only (the
 * form is short, so a second bar at the bottom only repeated it) and the Preview grid has no
 * Export / Maximize strip — its example rows are a picture of the rule, not data to take away.
 * RTCFR 2026-10-23: the Preview has no footer (every row shows), and an example permit + a tried
 * result are editable beside it (ExamplePermitControls, validated inline, evaluated live). Since
 * the same day the Preview shows THIS RULE ONLY, unsaved changes included: its example rows and
 * limit boxes are built from the limit types the rule names (previewRows / exampleFieldsFor), never
 * a fixed list. Active is a toggle switch.
 * None of the example values is saved. It reuses the shared detail markup
 * (sd-wrap / sd-head / sd-actionbar / sd-body, SettingsDetailPage.css) rather than the config-driven
 * <SettingsDetailPage>, because a rule's condition editor and colour picker are not field types that
 * page knows — the same precedent as the Reagent Prep and Sequence Setup detail pages.
 *
 * Keyboard (standing rule 14): Ctrl+S Save · Ctrl+Enter Save and Close · Ctrl+Shift+S Save and New ·
 * Esc Cancel · Alt+← / Alt+→ previous / next rule. Leaving with unsaved edits asks first
 * (standing rule 15) — the draft is compared with the saved rule, so a custom widget (colour
 * swatch, limit chip) counts as an edit too. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { SectionPanel } from '../../../components/SectionPanel';
import { BusyIcon } from '../../../components/loading';
import { useHotkeys, type HotkeyDef } from '../../../components/hotkeys';
import { useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { guardedExit, markUserEdit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { permitRowRule } from '../../analysis/permitLimit';
import { permitRuleClass, type RowColorCondition, type RowColorRule } from '../../analysis/permitRowColorState';
import { ROW_COLOR_OP_GLYPH, describeRowColorRule, paintDraftPermitRule, type RowColorSet } from '../../analysis/permitRowColors';
import { createRowColorRule, rowColorRefusal, updateRowColorRule, type RowColorRuleInput } from './permitRowColorsApi';
import { ExamplePermitControls } from './ExamplePermitControls';
import {
  DEFAULT_EXAMPLE, DEFAULT_TRY_RESULT, EXAMPLE_UNITS, exampleFieldsFor, exampleLimits, exampleMdl, exampleText,
  limitValue, type ExampleField, type ExamplePermit,
} from './permitExample';
import '../../modules/SettingsDetailPage.css';
import './permitRowColors.css';

/** The audit FormName (CLAUDE.md §14b.2) — matches PermitRowColorsController.AuditFormName. */
export const AUDIT_FORM = 'Permit Row Colors';
const ENTITY = 'Permit Row Color';
const NAME_MAX = 100;
const MAX_CONDITIONS = 3;
/* token-exempt: these are row colours the lab picks (data), not theme colours. */
export const PRESETS = ['#f9cfdc', '#ffd9ad', '#fff4b8', '#d7f0dd', '#d6e8fb', '#e9ddfb', '#e5e7eb'];
const HEX = /^#[0-9a-fA-F]{6}$/;
const DRAFT_ID = 'draft';

/* Preview: example results built from the RULE being previewed, never a fixed list (owner request
   2026-10-23). For every limit type the rule names, its example value (permitExample.ts) gives three
   rows — just below, at, and just above it — plus one row below every limit and one "less than"
   result, so each boundary of the rule is shown. Shared with the list page. */
export interface PreviewRow { id: string; example: string; result: string; rule?: RowColorRule }

/** A value as a short result text: 4.5 · 0.45 · 5 (no float noise such as 4.4999999). */
const resultText = (n: number) => String(Number(n.toFixed(4)));

/** The example rows for the given rules + limit boxes, each with the rule (if any) that colours it. */
export function previewRows(rules: RowColorRule[], fields: ExampleField[], example: ExamplePermit = DEFAULT_EXAMPLE): PreviewRow[] {
  const limits = exampleLimits(example, fields);
  const mdl = exampleMdl(example, fields);
  const points = new Map<number, string[]>();
  const add = (v: number, label: string) => {
    if (v < 0) return;
    const key = Number(v.toFixed(4));
    points.set(key, [...(points.get(key) ?? []), label]);
  };
  const values = fields
    .map((f) => ({ label: f.label, v: limitValue(exampleText(example, f.key)) }))
    .filter((x): x is { label: string; v: number } => x.v !== null);
  for (const { label, v } of values) {
    const step = v > 0 ? v * 0.1 : 0.1;
    if (v > 0) add(v - step, `Just below ${label}`);
    add(v, `At ${label}`);
    add(v + step, `Just above ${label}`);
  }
  const lowest = values.length ? values.reduce((m, x) => (x.v < m.v ? x : m)) : undefined;
  if (lowest && lowest.v > 0) add(lowest.v / 2, 'Below every limit');

  const rows: { example: string; result: string }[] = [];
  if (lowest && lowest.v > 0) {
    rows.push({ example: `Reported as less than ${lowest.label}`, result: `<${resultText(lowest.v)}` });
  }
  for (const [v, labels] of [...points.entries()].sort(([x], [y]) => x - y)) {
    rows.push({ example: labels.join(' · '), result: resultText(v) });
  }
  return rows.map((r, i) => ({
    id: `x${i}`, ...r, rule: permitRowRule(rules, limits, r.result, EXAMPLE_UNITS, mdl),
  }));
}

/** Every generated example row fits on one page, so the footerless Preview never hides a row. */
export const PREVIEW_PAGE_SIZE = 200;

/** A rule as the Preview applies it: on its own, and evaluated even while it is switched off —
 *  the Preview answers "what does THIS rule colour", not "is it live". */
export const previewRuleAlone = (rule: RowColorRule): RowColorRule[] => [{ ...rule, isActive: true }];

export const PREVIEW_COLUMNS: AppColumn<PreviewRow>[] = [
  { field: 'example', header: 'Example', minWidth: 180, wrap: true },
  { field: 'result', header: 'Result', width: 90, align: 'right' },
  {
    field: 'rule', header: 'Colour from', minWidth: 160,
    text: (r) => r.rule?.name ?? 'Normal colour',
    render: (r) => (r.rule ? r.rule.name : <span className="prc-muted">Normal colour</span>),
  },
];

/** The configured colour as a row-class name, so the shared grid paints it like the result forms do. */
export const previewRowClass = (r: PreviewRow) => (r.rule ? permitRuleClass(r.rule) : undefined);

interface DraftCondition { op: string; match: 'any' | 'all'; limits: string[] }
interface Draft { name: string; color: string; isActive: boolean; conditions: DraftCondition[] }
interface DraftErrors { name?: string; color?: string; conditions: Record<number, string> }

const blankCondition = (): DraftCondition => ({ op: 'GreaterThenEqual', match: 'any', limits: [] });

function draftOf(rule: RowColorRule | undefined, taken: string[]): Draft {
  if (rule) {
    return {
      name: rule.name, color: rule.color, isActive: rule.isActive,
      conditions: rule.conditions.map((c) => ({ op: c.op, match: c.match, limits: c.limits.map((l) => l.id) })),
    };
  }
  return { name: '', color: PRESETS.find((c) => !taken.includes(c)) ?? PRESETS[4]!, isActive: true, conditions: [blankCondition()] };
}

/** Where the user goes after a successful save (BO-UI §4: stay · list · clear for the next one). */
export type AfterSave = 'stay' | 'close' | 'new';

export interface PermitRowColorDetailProps {
  /** The rule being edited; undefined for New. */
  rule?: RowColorRule;
  set: RowColorSet;
  /** Rule ids in the list's current order — drives the prev/next arrows. */
  orderedIds: string[];
  onHome?: () => void;
  onClose: () => void;
  onNavigate: (id: string) => void;
  /** After a save: the parent reloads, then stays / returns to the list / opens a blank New. */
  onSaved: (saved: RowColorRule | undefined, after: AfterSave) => void;
}

export function PermitRowColorDetail({ rule, set, orderedIds, onHome, onClose, onNavigate, onSaved }: PermitRowColorDetailProps) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const rules = set.rules;
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // The saved state the draft is compared with. A New rule's starting draft is fixed at open;
  // an edited rule's follows the saved rule, so after Save (stay) the form is clean again.
  const taken = rules.filter((r) => r.id !== rule?.id).map((r) => r.color);
  const [newBaseline] = useState<Draft>(() => draftOf(undefined, taken));
  const baseline = useMemo(() => (rule ? draftOf(rule, []) : newBaseline), [rule, newBaseline]);
  const [draft, setDraft] = useState<Draft>(baseline);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  useUnsavedGuard(isDirty);

  // ── names for limit ids (live options + whatever a saved rule already carries) ──
  const limitName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rules) for (const c of r.conditions) for (const l of c.limits) if (l.name) map.set(l.id, l.name);
    for (const o of set.limitTypes) map.set(o.id, o.name);
    return map;
  }, [rules, set.limitTypes]);

  /** Limit chips to offer in a condition: the live options, plus any inactive one it already has. */
  const limitChoices = (cond: DraftCondition) => {
    const live = set.limitTypes;
    const extra = cond.limits.filter((id) => !live.some((o) => o.id === id))
      .map((id) => ({ id, name: limitName.get(id) ?? '(deleted)' }));
    return [...live, ...extra];
  };

  const draftConditions: RowColorCondition[] = draft.conditions.map((c) => ({
    op: c.op, match: c.match, limits: c.limits.map((id) => ({ id, name: id === 'MDL' ? 'MDL' : limitName.get(id) ?? null })),
  }));
  const draftText = describeRowColorRule(draftConditions);
  const draftRule: RowColorRule = {
    id: DRAFT_ID, name: draft.name.trim() || '(this rule)', color: HEX.test(draft.color) ? draft.color : '#ffffff',
    isActive: draft.isActive, sortOrder: rule?.sortOrder ?? rules.length + 1, conditions: draftConditions, text: draftText,
  };
  // The Preview shows what THIS rule does — the rule being edited, on its own, with example rows
  // built around the limits it names (owner request 2026-10-23: driven by the rule, not a fixed list).
  const previewRules = previewRuleAlone(draftRule);
  const previewFields = exampleFieldsFor(previewRules);
  // The example permit + tried result: preview-only, never saved, and not part of the dirty check.
  const [example, setExample] = useState<ExamplePermit>(DEFAULT_EXAMPLE);
  const [tryValue, setTryValue] = useState(DEFAULT_TRY_RESULT);
  const rows = previewRows(previewRules, previewFields, example);

  // The unsaved rule's colour exists only while the detail is open.
  const draftColor = draftRule.color;
  useEffect(() => {
    paintDraftPermitRule({ ...draftRule, color: draftColor });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- repaint only when the colour changes
  }, [draftColor]);
  useEffect(() => () => paintDraftPermitRule(null), []);

  function setCondition(i: number, patch: Partial<DraftCondition>) {
    setDraft((d) => ({ ...d, conditions: d.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  }
  function toggleLimit(i: number, id: string, on: boolean) {
    const order = set.limitTypes.map((o) => o.id);
    setDraft((d) => ({
      ...d,
      conditions: d.conditions.map((c, j) => {
        if (j !== i) return c;
        const next = on ? [...c.limits, id] : c.limits.filter((x) => x !== id);
        return { ...c, limits: next.sort((a, b) => order.indexOf(a) - order.indexOf(b)) };
      }),
    }));
  }
  function pickColor(color: string, from: Element) {
    setDraft((d) => ({ ...d, color }));
    markUserEdit(from);   // a swatch is a button, not an input the tracker can see (standing rule 15)
  }

  /** Field-level problems, mirroring the server so the user is told before a round trip (BO-UI §13 tier 3). */
  const errors: DraftErrors = (() => {
    const e: DraftErrors = { conditions: {} };
    const name = draft.name.trim();
    const others = rules.filter((r) => r.id !== rule?.id);
    if (!name) e.name = 'Name must not be empty.';
    else if (others.some((r) => r.name.toLowerCase() === name.toLowerCase())) e.name = `A rule named '${name}' already exists.`;
    if (!HEX.test(draft.color)) e.color = 'Colour must be a colour such as #F9CFDC.';
    else if (others.some((r) => r.color.toLowerCase() === draft.color.toLowerCase())) e.color = 'Another rule already uses this colour.';
    draft.conditions.forEach((c, i) => { if (c.limits.length === 0) e.conditions[i] = 'Pick at least one Limit Type.'; });
    return e;
  })();
  const hasErrors = !!errors.name || !!errors.color || Object.keys(errors.conditions).length > 0;

  function doSave(after: AfterSave) {
    if (saving) return;
    if (hasErrors) { setShowErrors(true); return; }
    const input: RowColorRuleInput = {
      name: draft.name.trim(), color: draft.color.toLowerCase(), isActive: draft.isActive,
      conditions: draft.conditions.map((c) => ({ op: c.op, match: c.match, limits: c.limits })),
    };
    let saved: RowColorRule | undefined;
    setSaving(true);
    void runSave({
      formName: AUDIT_FORM,
      auditAction: rule ? 'edit' : 'create',
      save: async () => { saved = rule ? await updateRowColorRule(rule.id, input) : await createRowColorRule(input); },
      onSaved: () => { setShowErrors(false); onSaved(saved, after); },
      onError: (e) => notify.error(rowColorRefusal(e) ?? 'The colour rule could not be saved.'),
    }).finally(() => setSaving(false));
  }

  // ── leaving (Cancel · breadcrumb · prev/next) asks first when there are unsaved edits ──
  const leave = (go: () => void) => () => { void guardedExit(go); };
  const pos = rule ? orderedIds.indexOf(rule.id) : -1;
  const prevId = pos > 0 ? orderedIds[pos - 1] : undefined;
  const nextId = pos >= 0 && pos < orderedIds.length - 1 ? orderedIds[pos + 1] : undefined;

  const keys: HotkeyDef[] = [
    { combo: 'mod+s', label: 'Save', group: 'Detail', order: 1, disabled: saving, run: () => doSave('stay') },
    { combo: 'mod+shift+s', label: 'Save and New', group: 'Detail', order: 2, disabled: saving, run: () => doSave('new') },
    { combo: 'mod+enter', label: 'Save and Close', group: 'Detail', order: 3, disabled: saving, run: () => doSave('close') },
    { combo: 'alt+left', label: 'Previous record', group: 'Detail', order: 7, disabled: !prevId || saving, run: () => prevId && leave(() => onNavigate(prevId))() },
    { combo: 'alt+right', label: 'Next record', group: 'Detail', order: 8, disabled: !nextId || saving, run: () => nextId && leave(() => onNavigate(nextId))() },
    { combo: 'esc', label: 'Cancel', group: 'Detail', order: 9, disabled: saving, allowInInput: false, run: () => leave(onClose)() },
  ];
  useHotkeys(keys);

  const recordName = rule ? rule.name : 'New';
  const err = (msg?: string) => (showErrors && msg ? <span className="sd-field-error">{msg}</span> : null);

  const ActionBar = () => (
    <div className="sd-actionbar">
      <button type="button" className="sd-abtn primary" onClick={() => doSave('stay')} disabled={saving}>
        {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
      </button>
      <button type="button" className="sd-abtn" onClick={() => doSave('close')} disabled={saving}>
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button type="button" className="sd-abtn" onClick={() => doSave('new')} disabled={saving}>
        <span className="tb-i">📄</span> Save and New
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" onClick={leave(onClose)} disabled={saving}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  return (
    <div className="bl-page mlv-host prc-page">
      <div className="bl-grid-area">
        <div className="sd-wrap" ref={wrapRef}>
          <div className="sd-head">
            <div>
              <h2>{ENTITY} – {recordName}</h2>
              <div className="sd-bc">
                <a onClick={leave(() => onHome?.())}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>Settings</span>
                <i className="ti ti-chevron-right" />
                <span>Permit Library</span>
                <i className="ti ti-chevron-right" />
                <a onClick={leave(onClose)}>Permit Row Colors</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="prc-head-right">
              <AuditTrailButton variant="chip" formName={AUDIT_FORM} sourceId={rule?.id} />
              <div className="sd-navarrows">
                <button type="button" className="sd-na" title="Previous record" disabled={!prevId || saving}
                  onClick={() => prevId && leave(() => onNavigate(prevId))()}>‹</button>
                <button type="button" className="sd-na next" title="Next record" disabled={!nextId || saving}
                  onClick={() => nextId && leave(() => onNavigate(nextId))()}>›</button>
              </div>
            </div>
          </div>

          <ActionBar />

          <div className="sd-body">
            <h3 className="sd-section-h">General Information</h3>
            <div className="sd-grid sd-grid--side prc-form">
              <div className="sd-row">
                <label className="sd-label" htmlFor="prc-name">Name<span className="req">*</span></label>
                <input id="prc-name" type="text" className={`sd-input${draft.name.trim() ? '' : ' required-empty'}`}
                  maxLength={NAME_MAX} value={draft.name} placeholder="e.g. MCL reached" aria-required="true"
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                {err(errors.name)}
              </div>
              <div className="sd-row sd-row--check">
                <label className="sd-label" htmlFor="prc-active">Active</label>
                {/* A toggle switch (owner request 2026-10-23) — still a native checkbox underneath, so
                    the unsaved-changes tracker and Space-to-toggle work unchanged. */}
                <input id="prc-active" type="checkbox" role="switch" aria-checked={draft.isActive} className="prc-switch"
                  title={draft.isActive ? 'Active — this rule colours rows' : 'Inactive — this rule is skipped'}
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))} />
              </div>
              <div className="sd-row full">
                <span className="sd-label">Colour<span className="req">*</span></span>
                <div className="prc-colors" role="radiogroup" aria-label="Row colour">
                  {PRESETS.map((c) => (
                    <button key={c} type="button" role="radio" aria-checked={draft.color === c} aria-label={c} title={c}
                      className={`prc-preset${draft.color === c ? ' on' : ''}`} style={{ background: c }}
                      onClick={(e) => pickColor(c, e.currentTarget)} />
                  ))}
                  <label className="prc-custom">
                    Custom
                    <input type="color" value={HEX.test(draft.color) ? draft.color : '#ffffff'} aria-label="Other colour"
                      onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value.toLowerCase() }))} />
                  </label>
                  <span className="prc-hex">{draft.color}</span>
                </div>
                {err(errors.color)}
              </div>
            </div>

            <h3 className="sd-section-h prc-section">Conditions</h3>
            <div className="sd-grid sd-grid--side prc-form prc-form--one">
              {draft.conditions.map((c, i) => (
                <div className="sd-row prc-cond-row" key={i}>
                  <span className={`sd-label${i ? ' prc-and' : ''}`}>
                    {i ? 'AND result' : <>When result<span className="req">*</span></>}
                  </span>
                  <div className="prc-cond">
                    <div className="prc-cond-head">
                      <select className="sd-input prc-op" value={c.op} aria-label={`Condition ${i + 1} operator`}
                        onChange={(e) => setCondition(i, { op: e.target.value })}>
                        {/* The condition's own operator is always offered, even if Utility ▸ Permit ▸ Operators
                            no longer lists it (or lists nothing) — otherwise the box renders blank and hides
                            what the saved rule actually says. */}
                        {!set.operators.some((o) => o.value === c.op) && (
                          <option value={c.op}>{ROW_COLOR_OP_GLYPH[c.op] ?? c.op}  {c.op}</option>
                        )}
                        {set.operators.map((o) => <option key={o.value} value={o.value}>{o.display}  {o.name}</option>)}
                      </select>
                      <select className="sd-input prc-match" value={c.match} aria-label={`Condition ${i + 1} any or all`}
                        title="any of = at least one of the limits · all of = every limit the permit has"
                        onChange={(e) => setCondition(i, { match: e.target.value as 'any' | 'all' })}>
                        <option value="any">any of</option>
                        <option value="all">all of</option>
                      </select>
                      <span className="prc-muted">these limits</span>
                      {i > 0 && (
                        <button type="button" className="ca-btn prc-remove" aria-label={`Remove condition ${i + 1}`}
                          title="Remove this condition"
                          onClick={(e) => {
                            markUserEdit(e.currentTarget);
                            setDraft((d) => ({ ...d, conditions: d.conditions.filter((_, j) => j !== i) }));
                          }}>
                          <span className="tb-i tb-i-red">−</span> Remove
                        </button>
                      )}
                    </div>
                    <div className="prc-chips" role="group" aria-label={`Condition ${i + 1} limit types`}>
                      {limitChoices(c).map((o) => {
                        const on = c.limits.includes(o.id);
                        return (
                          <label key={o.id} className={`prc-chip${on ? ' on' : ''}`}>
                            <input type="checkbox" checked={on} onChange={(e) => toggleLimit(i, o.id, e.target.checked)} />
                            {o.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {err(errors.conditions[i])}
                </div>
              ))}

              <div className="sd-row">
                <span className="sd-label" aria-hidden="true" />
                <div>
                  <button type="button" className="ca-btn" disabled={draft.conditions.length >= MAX_CONDITIONS}
                    onClick={(e) => {
                      markUserEdit(e.currentTarget);
                      setDraft((d) => ({ ...d, conditions: [...d.conditions, { op: 'LessThan', match: 'all', limits: [] }] }));
                    }}>
                    <span className="tb-i tb-i-green">＋</span> And condition
                  </button>
                </div>
              </div>

              <div className="sd-row">
                <span className="sd-label">Rule</span>
                <div className="sd-input readonly prc-readback" aria-live="polite">
                  <span className="prc-swatch" style={{ background: draftRule.color }} />
                  <span>{draftText}</span>
                </div>
              </div>
            </div>

            <SectionPanel title="Preview" icon="ti-eye" note="This rule only, unsaved changes included"
              className="prc-preview-panel prc-detail-preview">
              {/* The grid and, BESIDE it, the example permit + Try a result (owner request 2026-10-23). */}
              <div className="prc-preview-split">
              {/* Every example row at once: no pager / page size / item count (RTCFR 2026-10-23) —
                  the page size is raised so hiding the footer never hides a row. */}
              <AppDataGrid
                rows={rows}
                columns={PREVIEW_COLUMNS}
                getRowId={(r) => r.id}
                getRowClassName={previewRowClass}
                className="prc-nozebra"
                exportFilename="Permit Row Colors Preview"
                emptyText="Tick a limit type in the conditions to see examples."
                defaultPageSize={PREVIEW_PAGE_SIZE}
                hideHeaderTools
                hideFooter
              />
              {/* The rule being edited, on its own, with its unsaved changes — so the user sees what
                  THIS rule colours before saving it. */}
              <ExamplePermitControls rules={previewRules} fields={previewFields} example={example}
                onExampleChange={setExample} tryValue={tryValue} onTryChange={setTryValue} vertical />
              </div>
            </SectionPanel>
          </div>
        </div>
      </div>
    </div>
  );
}
